import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Reservation, ReservationItem, ReservationStatus, ReservedQuantityMap, CartItem, TransferType } from '../types';
import { batchTransferItems, generateTransferNumber } from '../utils/locationTransfer';
import { notifyStaffCcForPosReservation } from '../lib/reservationStaffCcMail';
import { getValidAuthHeaders } from '../lib/auth';
import { fetchAllPaginated } from '../lib/fetchAllPaginated';
import { getAdminId as resolveAdminId } from '../utils/getAdminId';

interface CreateReservationData {
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  notes?: string;
  items: Array<{
    product_id: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    source_location_id?: string;
  }>;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
}

interface CreateTransferData {
  worker_id: string;
  worker_name: string;
  source_location_id: string;
  destination_location_id: string;
  notes?: string;
  items: Array<{
    product_id: string;
    quantity: number;
    unit_price?: number;
  }>;
}

interface ReceivedItem {
  item_id: string;
  product_id: string;
  received_quantity: number;
}

interface ReturnItemData {
  product_id: string;
  quantity: number;
  source_location_id: string;
  destination_location_id: string;
}

export interface FulfillmentAdjustment {
  itemId: string;
  productId: string;
  productName?: string;
  requested: number;
  fulfilled: number;
  reason: 'partial' | 'unavailable';
}

export interface ReservationMissingItem {
  item_id: string;
  product_id: string;
  name: string | null;
  reference_code: string | null;
  sku: string | null;
  color: string;
  size: string;
  requested: number;
  picked: number;
  available: number;
  transferred: number;
  reason: 'skipped' | 'insufficient_stock';
}

export interface FulfillReservationOptions {
  pickedItems?: Array<{ item_id: string; picked_qty: number }>;
  destinationLocationId?: string;
}

export interface ShopifySyncOutcome {
  attempted: boolean;
  ok: boolean;
  kind?: string;
  skipped?: Array<{ productId: string; sku?: string | null; qty?: number; reason: string }>;
}

export interface FulfillmentResult {
  success: boolean;
  adjustments: FulfillmentAdjustment[];
  missing?: ReservationMissingItem[];
  source?: string;
  shopifySync?: ShopifySyncOutcome;
  warning?: string;
  error?: string;
}

interface FulfillRpcResult {
  success: boolean;
  already_fulfilled?: boolean;
  error?: string;
  source?: string;
  shopify_order_id?: number | null;
  transfers?: Array<{ product_id: string; from_location_id: string; to_location_id: string; qty: number }>;
  missing?: ReservationMissingItem[];
  outbox_id?: string | null;
  outbox_kind?: string | null;
}

export const useReservations = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [reservedQuantities, setReservedQuantities] = useState<ReservedQuantityMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAdminId = useCallback(() => {
    if (!user) return null;
    return resolveAdminId(user);
  }, [user]);

  const loadReservations = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId();
    if (!adminId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await fetchAllPaginated<Reservation>((from, to) =>
        supabase
          .from('reservations')
          .select(`
          *,
          reservation_items (
            id,
            reservation_id,
            product_id,
            quantity,
            unit_price,
            total_price,
            source_location_id,
            created_at
          )
        `)
          .eq('admin_id', adminId)
          .order('created_at', { ascending: false })
          .range(from, to)
      );

      if (fetchError) throw fetchError;

      setReservations(data || []);
      await loadReservedQuantities();
    } catch (err) {
      setError('Failed to load reservations');
      console.error('Error loading reservations:', err);
    } finally {
      setLoading(false);
    }
  }, [user, getAdminId]);

  const loadReservedQuantities = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('reservation_items')
        .select(`
          product_id,
          quantity,
          reservations!inner (
            admin_id,
            status
          )
        `)
        .eq('reservations.admin_id', adminId)
        .in('reservations.status', ['reserved', 'ready']);

      if (fetchError) throw fetchError;

      const quantities: ReservedQuantityMap = {};
      (data || []).forEach((item: any) => {
        const productId = item.product_id;
        quantities[productId] = (quantities[productId] || 0) + item.quantity;
      });

      setReservedQuantities(quantities);
    } catch (err) {
      console.error('Error loading reserved quantities:', err);
    }
  }, [user, getAdminId]);

  useEffect(() => {
    if (user) {
      loadReservations();
    } else {
      setReservations([]);
      setReservedQuantities({});
      setError(null);
    }
  }, [user, loadReservations]);

  const generateReservationNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `RES-${year}${month}${day}-${random}`;
  };

  const createReservation = async (data: CreateReservationData): Promise<Reservation | null> => {
    if (!user) return null;

    const adminId = getAdminId();
    if (!adminId) {
      setError('Unable to determine admin for reservation');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const reservationNumber = generateReservationNumber();

      const { data: reservation, error: insertError } = await supabase
        .from('reservations')
        .insert({
          admin_id: adminId,
          reservation_number: reservationNumber,
          customer_name: data.customer_name,
          customer_email: data.customer_email || null,
          customer_phone: data.customer_phone || null,
          customer_address: data.customer_address || null,
          notes: data.notes || null,
          status: 'reserved',
          subtotal: data.subtotal,
          tax_amount: data.tax_amount,
          total_amount: data.total_amount,
          expires_at: expiresAt.toISOString(),
          reserved_by: user.id,
          reserved_at: new Date().toISOString()
        })
        .select()
        .single();

      if (insertError) throw insertError;

      if (data.items.length > 0) {
        const itemsToInsert = data.items.map(item => ({
          reservation_id: reservation.id,
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          source_location_id: item.source_location_id || null
        }));

        const { error: itemsError } = await supabase
          .from('reservation_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      const { data: completeReservation, error: fetchError } = await supabase
        .from('reservations')
        .select(`
          *,
          reservation_items (
            id,
            reservation_id,
            product_id,
            quantity,
            unit_price,
            total_price,
            created_at
          )
        `)
        .eq('id', reservation.id)
        .single();

      if (fetchError) throw fetchError;

      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user.id,
        action_type: 'reservation_created',
        entity_type: 'reservation',
        entity_id: reservation.id,
        description: `${user.full_name || user.email} created reservation ${reservationNumber} for ${data.customer_name}`,
        metadata: {
          reservation_number: reservationNumber,
          customer_name: data.customer_name,
          total_amount: data.total_amount,
          items_count: data.items.length
        },
        created_at: new Date().toISOString()
      });

      await loadReservations();
      void notifyStaffCcForPosReservation(adminId, completeReservation as Reservation).catch(err => {
        console.error('Staff reservation CC email failed:', err);
      });
      return completeReservation;
    } catch (err) {
      setError('Failed to create reservation');
      console.error('Error creating reservation:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateReservationStatus = async (
    reservationId: string,
    newStatus: ReservationStatus,
    reason?: string
  ): Promise<boolean> => {
    if (!user) return false;

    const adminId = getAdminId();
    if (!adminId) return false;

    setLoading(true);
    setError(null);

    try {
      const reservation = reservations.find(r => r.id === reservationId);
      if (!reservation) {
        setError('Reservation not found');
        return false;
      }

      const updateData: any = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'ready') {
        updateData.ready_at = new Date().toISOString();
        updateData.ready_marked_by = user.id;
      } else if (newStatus === 'cancelled') {
        updateData.cancelled_at = new Date().toISOString();
        updateData.cancelled_by = user.id;
        updateData.cancellation_reason = reason || null;
      } else if (newStatus === 'fulfilled') {
        updateData.fulfilled_at = new Date().toISOString();
        updateData.fulfilled_by = user.id;
      }

      // Release any active pick claim once the reservation leaves the picking queue.
      if (newStatus === 'ready' || newStatus === 'fulfilled' || newStatus === 'cancelled') {
        updateData.claimed_by = null;
        updateData.claimed_at = null;
      }

      const { error: updateError } = await supabase
        .from('reservations')
        .update(updateData)
        .eq('id', reservationId);

      if (updateError) throw updateError;

      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user.id,
        action_type: `reservation_${newStatus}`,
        entity_type: 'reservation',
        entity_id: reservationId,
        description: `${user.full_name || user.email} changed reservation ${reservation.reservation_number} status to ${newStatus}`,
        metadata: {
          reservation_number: reservation.reservation_number,
          previous_status: reservation.status,
          new_status: newStatus,
          reason: reason
        },
        created_at: new Date().toISOString()
      });

      await loadReservations();
      return true;
    } catch (err) {
      setError(`Failed to update reservation status to ${newStatus}`);
      console.error('Error updating reservation status:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fulfills a reservation through the atomic, race-safe server RPC, then performs the
   * source-aware Shopify side effect:
   *  - app-origin reservation  -> balanced inventory delta to every involved mapped location
   *  - Shopify store-pickup     -> label-less order fulfillment (NO inventory push)
   * The Shopify call is best-effort: the side effect is recorded durably in
   * shopify_sync_outbox by the RPC and retried by the reconcile processor on failure.
   *
   * Accepts either a legacy destinationLocationId string or an options object so the
   * Reports -> Reservations caller and the Reservation Terminal share one implementation.
   */
  const fulfillReservation = async (
    reservationId: string,
    optionsOrDestination?: FulfillReservationOptions | string
  ): Promise<FulfillmentResult> => {
    if (!user) return { success: false, adjustments: [], error: 'Not authenticated' };

    const adminId = getAdminId();
    if (!adminId) return { success: false, adjustments: [], error: 'Admin context not found' };

    const options: FulfillReservationOptions =
      typeof optionsOrDestination === 'string'
        ? { destinationLocationId: optionsOrDestination }
        : optionsOrDestination ?? {};

    setLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc('fulfill_reservation_atomic', {
        p_reservation_id: reservationId,
        p_admin_id: adminId,
        p_user_id: user.id,
        p_picked_items: options.pickedItems ?? null,
        p_destination_location_id: options.destinationLocationId ?? null,
      });

      if (rpcError) throw rpcError;

      const result = (data || {}) as FulfillRpcResult;

      if (!result.success) {
        setError(result.error || 'Failed to fulfill reservation');
        return { success: false, adjustments: [], error: result.error || 'Failed to fulfill reservation' };
      }

      const missing = result.missing ?? [];
      const adjustments: FulfillmentAdjustment[] = missing.map(m => ({
        itemId: m.item_id,
        productId: m.product_id,
        productName: m.name ?? undefined,
        requested: m.requested,
        fulfilled: m.transferred,
        reason: m.transferred === 0 ? 'unavailable' : 'partial',
      }));

      if (result.already_fulfilled) {
        await loadReservations();
        return { success: true, adjustments, missing, source: result.source };
      }

      let shopifySync: ShopifySyncOutcome = { attempted: false, ok: true };
      let warning: string | undefined;

      if (result.outbox_id && result.outbox_kind) {
        // Captured before any throw so the failure path can persist what did not pass.
        let respSkipped: Array<{ productId: string; sku?: string | null; qty?: number; reason: string }> = [];
        try {
          const headers = await getValidAuthHeaders();
          if (!headers) throw new Error('Not authenticated for Shopify sync');

          const base = import.meta.env.VITE_SUPABASE_URL;
          let resp: Response;

          if (result.outbox_kind === 'order_fulfill') {
            resp = await fetch(`${base}/functions/v1/shopify-fulfill-reservation`, {
              method: 'POST',
              headers,
              body: JSON.stringify({ shopifyOrderId: result.shopify_order_id, notifyCustomer: false }),
            });
          } else {
            const transfers = (result.transfers ?? []).map(t => ({
              productId: t.product_id,
              fromLocationId: t.from_location_id,
              toLocationId: t.to_location_id,
              qty: t.qty,
            }));
            resp = await fetch(`${base}/functions/v1/shopify-adjust-inventory`, {
              method: 'POST',
              headers,
              body: JSON.stringify({ transfers }),
            });
          }

          const syncData = await resp.json().catch(() => ({}));
          if (Array.isArray(syncData.skipped)) respSkipped = syncData.skipped;

          if (resp.ok && syncData?.success) {
            shopifySync = {
              attempted: true,
              ok: true,
              kind: result.outbox_kind,
              skipped: respSkipped.length > 0 ? respSkipped : undefined,
            };
            await supabase
              .from('shopify_sync_outbox')
              .update({
                status: 'synced',
                skipped_items: respSkipped,
                updated_at: new Date().toISOString(),
              })
              .eq('id', result.outbox_id);

            if (respSkipped.length > 0) {
              warning = 'Some locations or items are not mapped to Shopify and were skipped during the push.';
            }
          } else {
            throw new Error(syncData?.error || `Shopify sync failed (${resp.status})`);
          }
        } catch (syncErr) {
          const msg = syncErr instanceof Error ? syncErr.message : 'Shopify sync failed';
          shopifySync = { attempted: true, ok: false, kind: result.outbox_kind ?? undefined };
          warning = `Stock was updated locally. Shopify sync did not complete and will retry automatically: ${msg}`;
          // Leave status 'pending' and let the outbox processor own attempt counting.
          // Persist any skipped detail so the "Need control" review shows what did not pass.
          await supabase
            .from('shopify_sync_outbox')
            .update({ last_error: msg, skipped_items: respSkipped, updated_at: new Date().toISOString() })
            .eq('id', result.outbox_id);
        }
      }

      await loadReservations();
      return { success: true, adjustments, missing, source: result.source, shopifySync, warning };
    } catch (err) {
      setError('Failed to fulfill reservation');
      console.error('Error fulfilling reservation:', err);
      return {
        success: false,
        adjustments: [],
        error: err instanceof Error ? err.message : 'Failed to fulfill reservation',
      };
    } finally {
      setLoading(false);
    }
  };

  const cancelReservation = async (reservationId: string, reason?: string): Promise<boolean> => {
    return updateReservationStatus(reservationId, 'cancelled', reason);
  };

  const reactivateReservation = async (
    reservationId: string,
    newExpiresAtIso: string
  ): Promise<boolean> => {
    if (!user) return false;

    const adminId = getAdminId();
    if (!adminId) return false;

    const newExpiryDate = new Date(newExpiresAtIso);
    if (Number.isNaN(newExpiryDate.getTime())) {
      setError('Invalid expiry date');
      return false;
    }
    if (newExpiryDate.getTime() <= Date.now()) {
      setError('New expiry date must be in the future');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const reservation = reservations.find(r => r.id === reservationId);
      if (!reservation) {
        setError('Reservation not found');
        return false;
      }
      if (reservation.status !== 'expired') {
        setError('Only expired reservations can be reactivated');
        return false;
      }

      const previousExpiresAt = reservation.expires_at;
      const nowIso = new Date().toISOString();

      const { error: updateError } = await supabase
        .from('reservations')
        .update({
          status: 'reserved',
          expires_at: newExpiresAtIso,
          updated_at: nowIso,
          cancelled_at: null,
          cancelled_by: null,
          cancellation_reason: null,
          fulfilled_at: null,
          fulfilled_by: null,
          ready_at: null,
          ready_marked_by: null,
        })
        .eq('id', reservationId);

      if (updateError) throw updateError;

      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user.id,
        action_type: 'reservation_reactivated',
        entity_type: 'reservation',
        entity_id: reservationId,
        description: `${user.full_name || user.email} reactivated reservation ${reservation.reservation_number} until ${newExpiryDate.toLocaleDateString()}`,
        metadata: {
          reservation_number: reservation.reservation_number,
          previous_status: 'expired',
          new_status: 'reserved',
          previous_expires_at: previousExpiresAt,
          new_expires_at: newExpiresAtIso,
        },
        created_at: nowIso,
      });

      await loadReservations();
      return true;
    } catch (err) {
      setError('Failed to reactivate reservation');
      console.error('Error reactivating reservation:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const checkExpiredReservations = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const now = new Date().toISOString();

      // Shopify store-pickup reservations represent PAID online orders and must never
      // auto-expire; operators retire them manually from the Reservation Terminal.
      const { data: expiredReservations, error: fetchError } = await supabase
        .from('reservations')
        .select('id, reservation_number')
        .eq('admin_id', adminId)
        .in('status', ['reserved', 'ready'])
        .or('source.is.null,source.neq.shopify')
        .lt('expires_at', now);

      if (fetchError) throw fetchError;

      for (const reservation of expiredReservations || []) {
        await supabase
          .from('reservations')
          .update({
            status: 'expired',
            updated_at: new Date().toISOString()
          })
          .eq('id', reservation.id);

        await supabase.from('activity_logs').insert({
          admin_id: adminId,
          user_id: user.id,
          action_type: 'reservation_expired',
          entity_type: 'reservation',
          entity_id: reservation.id,
          description: `Reservation ${reservation.reservation_number} automatically expired`,
          metadata: { reservation_number: reservation.reservation_number },
          created_at: new Date().toISOString()
        });
      }

      if ((expiredReservations?.length || 0) > 0) {
        await loadReservations();
      }
    } catch (err) {
      console.error('Error checking expired reservations:', err);
    }
  }, [user, getAdminId, loadReservations]);

  useEffect(() => {
    if (user) {
      checkExpiredReservations();
      const interval = setInterval(checkExpiredReservations, 60000);
      return () => clearInterval(interval);
    }
  }, [user, checkExpiredReservations]);

  const getReservationById = (id: string): Reservation | undefined => {
    return reservations.find(r => r.id === id);
  };

  const getReservationsByStatus = (status: ReservationStatus): Reservation[] => {
    return reservations.filter(r => r.status === status);
  };

  const getActiveReservations = (): Reservation[] => {
    return reservations.filter(r => r.status === 'reserved' || r.status === 'ready');
  };

  const getReservationsByCustomer = (customerName: string): Reservation[] => {
    return reservations.filter(r =>
      r.customer_name.toLowerCase().includes(customerName.toLowerCase())
    );
  };

  const getReservedQuantityForProduct = (productId: string): number => {
    return reservedQuantities[productId] || 0;
  };

  const createInternalTransfer = async (data: CreateTransferData): Promise<Reservation | null> => {
    if (!user) return null;

    const adminId = getAdminId();
    if (!adminId) {
      setError('Unable to determine admin for transfer');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const transferNumber = generateTransferNumber();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      const { data: transfer, error: insertError } = await supabase
        .from('reservations')
        .insert({
          admin_id: adminId,
          reservation_number: transferNumber,
          customer_name: data.worker_name,
          status: 'pending_receipt',
          transfer_type: 'internal_transfer',
          source_location_id: data.source_location_id,
          destination_location_id: data.destination_location_id,
          worker_id: data.worker_id,
          worker_name: data.worker_name,
          notes: data.notes || null,
          subtotal: 0,
          tax_amount: 0,
          total_amount: 0,
          expires_at: expiresAt.toISOString(),
          reserved_by: user.id,
          reserved_at: new Date().toISOString()
        })
        .select()
        .single();

      if (insertError) throw insertError;

      if (data.items.length > 0) {
        const itemsToInsert = data.items.map(item => ({
          reservation_id: transfer.id,
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price || 0,
          total_price: (item.unit_price || 0) * item.quantity,
          source_location_id: data.source_location_id,
          destination_location_id: data.destination_location_id,
          received_quantity: 0
        }));

        const { error: itemsError } = await supabase
          .from('reservation_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      const { data: completeTransfer, error: fetchError } = await supabase
        .from('reservations')
        .select(`
          *,
          reservation_items (
            id,
            reservation_id,
            product_id,
            quantity,
            unit_price,
            total_price,
            source_location_id,
            destination_location_id,
            received_quantity,
            transferred_at,
            created_at
          )
        `)
        .eq('id', transfer.id)
        .single();

      if (fetchError) throw fetchError;

      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user.id,
        action_type: 'transfer_created',
        entity_type: 'reservation',
        entity_id: transfer.id,
        description: `${user.full_name || user.email} created transfer ${transferNumber}`,
        metadata: {
          transfer_number: transferNumber,
          worker_name: data.worker_name,
          source_location_id: data.source_location_id,
          destination_location_id: data.destination_location_id,
          items_count: data.items.length
        },
        created_at: new Date().toISOString()
      });

      await loadReservations();
      return completeTransfer;
    } catch (err) {
      setError('Failed to create transfer');
      console.error('Error creating transfer:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const confirmTransferReceipt = async (
    transferId: string,
    receivedItems: ReceivedItem[],
    receivingWorkerId: string
  ): Promise<boolean> => {
    if (!user) return false;

    const adminId = getAdminId();
    if (!adminId) return false;

    setLoading(true);
    setError(null);

    try {
      const transfer = reservations.find(r => r.id === transferId);
      if (!transfer) {
        setError('Transfer not found');
        return false;
      }

      if (transfer.transfer_type !== 'internal_transfer') {
        setError('Not an internal transfer');
        return false;
      }

      const transferItems = transfer.reservation_items || [];
      const itemsToTransfer = receivedItems
        .filter(ri => ri.received_quantity > 0)
        .map(ri => {
          const originalItem = transferItems.find(ti => ti.id === ri.item_id);
          return {
            productId: ri.product_id,
            quantity: ri.received_quantity,
            sourceLocationId: transfer.source_location_id!,
            destinationLocationId: transfer.destination_location_id!
          };
        });

      if (itemsToTransfer.length > 0) {
        const transferResult = await batchTransferItems(itemsToTransfer, adminId);
        if (!transferResult.success) {
          setError(transferResult.error || 'Failed to transfer inventory');
          return false;
        }
      }

      for (const ri of receivedItems) {
        const { error: updateItemError } = await supabase
          .from('reservation_items')
          .update({
            received_quantity: ri.received_quantity,
            transferred_at: ri.received_quantity > 0 ? new Date().toISOString() : null
          })
          .eq('id', ri.item_id);

        if (updateItemError) throw updateItemError;
      }

      const allFullyReceived = transferItems.every(ti => {
        const received = receivedItems.find(ri => ri.item_id === ti.id);
        return received && received.received_quantity >= ti.quantity;
      });

      const anyReceived = receivedItems.some(ri => ri.received_quantity > 0);

      let newStatus: ReservationStatus;
      if (allFullyReceived) {
        newStatus = 'transferred';
      } else if (anyReceived) {
        newStatus = 'partial_receipt';
      } else {
        newStatus = 'pending_receipt';
      }

      const { error: updateError } = await supabase
        .from('reservations')
        .update({
          status: newStatus,
          received_by: receivingWorkerId,
          received_at: anyReceived ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', transferId);

      if (updateError) throw updateError;

      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user.id,
        action_type: allFullyReceived ? 'transfer_received' : 'transfer_partial',
        entity_type: 'reservation',
        entity_id: transferId,
        description: `${user.full_name || user.email} ${allFullyReceived ? 'fully received' : 'partially received'} transfer ${transfer.reservation_number}`,
        metadata: {
          transfer_number: transfer.reservation_number,
          items_received: receivedItems.filter(ri => ri.received_quantity > 0).length,
          total_items: transferItems.length
        },
        created_at: new Date().toISOString()
      });

      await loadReservations();
      return true;
    } catch (err) {
      setError('Failed to confirm transfer receipt');
      console.error('Error confirming transfer:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const returnToWarehouse = async (
    items: ReturnItemData[],
    workerId: string,
    workerName: string,
    notes?: string,
    originalTransferId?: string
  ): Promise<Reservation | null> => {
    if (!user) return null;

    const adminId = getAdminId();
    if (!adminId) {
      setError('Unable to determine admin for return');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const transferNumber = generateTransferNumber();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      const sourceLocationId = items[0].source_location_id;
      const destinationLocationId = items[0].destination_location_id;

      const { data: transfer, error: insertError } = await supabase
        .from('reservations')
        .insert({
          admin_id: adminId,
          reservation_number: transferNumber,
          customer_name: workerName,
          status: 'pending_receipt',
          transfer_type: 'internal_transfer',
          source_location_id: sourceLocationId,
          destination_location_id: destinationLocationId,
          worker_id: workerId,
          worker_name: workerName,
          notes: notes || 'Return to warehouse',
          original_transfer_id: originalTransferId || null,
          subtotal: 0,
          tax_amount: 0,
          total_amount: 0,
          expires_at: expiresAt.toISOString(),
          reserved_by: user.id,
          reserved_at: new Date().toISOString()
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const itemsToInsert = items.map(item => ({
        reservation_id: transfer.id,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: 0,
        total_price: 0,
        source_location_id: item.source_location_id,
        destination_location_id: item.destination_location_id,
        received_quantity: 0
      }));

      const { error: itemsError } = await supabase
        .from('reservation_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      const { data: completeTransfer, error: fetchError } = await supabase
        .from('reservations')
        .select(`
          *,
          reservation_items (*)
        `)
        .eq('id', transfer.id)
        .single();

      if (fetchError) throw fetchError;

      await supabase.from('activity_logs').insert({
        admin_id: adminId,
        user_id: user.id,
        action_type: 'transfer_returned',
        entity_type: 'reservation',
        entity_id: transfer.id,
        description: `${user.full_name || user.email} created return transfer ${transferNumber}`,
        metadata: {
          transfer_number: transferNumber,
          worker_name: workerName,
          source_location_id: sourceLocationId,
          destination_location_id: destinationLocationId,
          items_count: items.length,
          original_transfer_id: originalTransferId
        },
        created_at: new Date().toISOString()
      });

      await loadReservations();
      return completeTransfer;
    } catch (err) {
      setError('Failed to create return transfer');
      console.error('Error creating return:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getPendingTransfers = useCallback((): Reservation[] => {
    return reservations.filter(r =>
      r.transfer_type === 'internal_transfer' &&
      (r.status === 'pending_receipt' || r.status === 'partial_receipt')
    );
  }, [reservations]);

  const getTransfersByLocation = useCallback((locationId: string): Reservation[] => {
    return reservations.filter(r =>
      r.transfer_type === 'internal_transfer' &&
      (r.source_location_id === locationId || r.destination_location_id === locationId)
    );
  }, [reservations]);

  const getCustomerReservations = useCallback((): Reservation[] => {
    return reservations.filter(r =>
      r.transfer_type !== 'internal_transfer' || !r.transfer_type
    );
  }, [reservations]);

  const getInternalTransfers = useCallback((): Reservation[] => {
    return reservations.filter(r => r.transfer_type === 'internal_transfer');
  }, [reservations]);

  return {
    reservations,
    reservedQuantities,
    loading,
    error,
    createReservation,
    updateReservationStatus,
    fulfillReservation,
    cancelReservation,
    reactivateReservation,
    refreshReservations: loadReservations,
    getReservationById,
    getReservationsByStatus,
    getActiveReservations,
    getReservationsByCustomer,
    getReservedQuantityForProduct,
    checkExpiredReservations,
    createInternalTransfer,
    confirmTransferReceipt,
    returnToWarehouse,
    getPendingTransfers,
    getTransfersByLocation,
    getCustomerReservations,
    getInternalTransfers
  };
};
